import { FC, useEffect, useState } from "react";
import { Input } from "../../../design-system";
import { renameProject } from "../../../common/api/ProjectClient";
import { handleError } from "../../../common/ErrorHandling";
import { EditableField } from "../../../common/components/EditableField";
import styles from "./ProjectNameWidget.module.less";

interface ProjectNameWidgetProps {
    projectId: number;
    name: string;
    vendorName?: string;
    canEdit: boolean;
    onUpdate: () => void;
}

/**
 * The project's name with the vendor under it, as the hub's project list
 * shows them. When the user can edit, hovering the name reveals an edit
 * affordance and clicking turns it into an input; Enter or leaving it saves.
 */
export const ProjectNameWidget: FC<ProjectNameWidgetProps> = ({
    projectId,
    name,
    vendorName,
    canEdit,
    onUpdate,
}) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(name || "");

    useEffect(() => {
        setValue(name || "");
    }, [name]);

    const handleSave = async () => {
        const trimmed = value.trim();
        if (trimmed && trimmed !== name) {
            try {
                await renameProject(projectId, trimmed);
                onUpdate();
            } catch (error) {
                handleError(error);
                setValue(name || "");
            }
        } else {
            setValue(name || "");
        }
        setEditing(false);
    };

    return (
        // A row around this may navigate on click; editing must not.
        <div className={styles.container} onClick={(e) => e.stopPropagation()}>
            {canEdit && editing ? (
                <Input
                    autoFocus
                    size="small"
                    className={styles.input}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={handleSave}
                    onPressEnter={handleSave}
                />
            ) : (
                <EditableField canEdit={canEdit} onClick={() => setEditing(true)} fill>
                    <span className={styles.name}>{name || "Unnamed project"}</span>
                </EditableField>
            )}
            {vendorName && <span className={styles.vendor}>{vendorName}</span>}
        </div>
    );
};
